import { logInfo } from 'app/services/logger';

import {
  ContactFormItem,
  FormItemButton,
  FormItemCheckbox,
  FormItemImage,
  FormItemInput,
  FormItemSingleChoice,
  FormItemText,
  FormItemType,
  FormItemVideo,
} from '../types';
import ContactFormButtonFactory from './ContactFormButtonFactory';
import { ContactFormCheckbox } from './ContactFormCheckbox';
import { ContactFormImage } from './ContactFormImage';
import { ContactFormInput } from './ContactFormInput';
import { ContactFormMultiChoice } from './ContactFormMultiChoice';
import { ContactFormSingleChoice } from './ContactFormSingleChoice';
import ContactFormStaticText from './ContactFormStaticText';
import { ContactFormVideo } from './ContactFormVideo';

type Props = {
  item: ContactFormItem;
  isSubmitted: boolean;
  hasErrors: boolean;
  isValid: boolean;
  isFetching: boolean;
  buttonVariant: 'default' | 'convoai';
  onClickCancel?: () => void;
  isComplete: boolean;
  showSpacer?: boolean;
  dirtyFields: string[];
  setFieldDirty: (field: string) => void;
  contactFormSlug?: string;
};

export default function ContactFormItemFactory({
  item,
  isSubmitted,
  hasErrors,
  isValid,
  isFetching,
  buttonVariant,
  onClickCancel,
  isComplete,
  showSpacer = false,
  dirtyFields,
  setFieldDirty,
  contactFormSlug,
}: Props) {
  switch (item.item_type) {
    case FormItemType.text: {
      return (
        <ContactFormStaticText
          item={item as FormItemText}
          contactFormSlug={contactFormSlug!}
        />
      );
    }

    case FormItemType.button: {
      return (
        <ContactFormButtonFactory
          item={item as FormItemButton}
          variant={buttonVariant}
          hasErrors={hasErrors}
          isValid={isValid}
          disabled={isFetching}
          onClickCancel={onClickCancel ?? (() => {})}
          isComplete={isComplete}
        />
      );
    }

    case FormItemType.input: {
      return (
        <ContactFormInput
          item={item as FormItemInput}
          isSubmitted={isSubmitted}
          isDirty={dirtyFields.includes(item.ref_id)}
          onChange={() => setFieldDirty(item.ref_id)}
        />
      );
    }

    case FormItemType.dynamic_spacer: {
      return showSpacer ? <div className="grow" /> : null;
    }

    case FormItemType.choice: {
      return item.selector_type === 'single' ? (
        <ContactFormSingleChoice
          item={item as FormItemSingleChoice}
          isSubmitted={isSubmitted}
          contactFormSlug={contactFormSlug}
        />
      ) : (
        <ContactFormMultiChoice
          item={item as FormItemSingleChoice}
          isSubmitted={isSubmitted}
          contactFormSlug={contactFormSlug}
        />
      );
    }
    case FormItemType.checkbox: {
      return (
        <ContactFormCheckbox
          item={item as FormItemCheckbox}
          isSubmitted={isSubmitted}
          contactFormSlug={contactFormSlug}
        />
      );
    }
    case FormItemType.image: {
      return <ContactFormImage item={item as FormItemImage} />;
    }
    case FormItemType.video: {
      return <ContactFormVideo item={item as FormItemVideo} />;
    }
    default: {
      logInfo('Item type:', item.item_type, 'not implemented');
      return null;
    }
  }
}
